// Excel-style column headers: click the name, get Sort and a checklist of every
// value in the column. State is the page's own (not the URL); the menu only
// reads and reports it. useColumnView wires a table's columns in one call.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCheckbox, SpecraPopover } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../../lib/utils';
import { applyColumnView, distinctValues } from '../lib/columnView';
const SORT_LABEL = {
    text: {
        asc: 'Sort A→Z',
        desc: 'Sort Z→A'
    },
    date: {
        asc: 'Oldest first',
        desc: 'Newest first'
    }
};
export function ColumnMenu({ columnId, label, kind = 'text', options, selected, onSelectedChange, sort = null, onSortChange, filterable = true }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const filtered = filterable && selected.size > 0;
    const labels = SORT_LABEL[kind];
    const toggle = (v, on)=>{
        const next = new Set(selected);
        if (on) next.add(v);
        else next.delete(v);
        onSelectedChange(next);
    };
    const sortButton = (dir, testId)=>/*#__PURE__*/ _jsxs(SpecraButton, {
            size: "sm",
            variant: sort === dir ? 'secondary' : 'ghost',
            className: "w-full justify-start",
            "aria-pressed": sort === dir,
            onClick: ()=>onSortChange?.(sort === dir ? null : dir),
            "data-testid": testId,
            children: [
                dir === 'asc' ? /*#__PURE__*/ _jsx(ArrowUp, {
                    "aria-hidden": true,
                    className: "size-3.5"
                }) : /*#__PURE__*/ _jsx(ArrowDown, {
                    "aria-hidden": true,
                    className: "size-3.5"
                }),
                labels[dir]
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraButton, {
                ref: setAnchor,
                size: "sm",
                variant: "ghost",
                className: "group -ml-2 h-auto gap-1 px-2 py-0.5 font-medium",
                "aria-haspopup": "dialog",
                "aria-expanded": open,
                onClick: (e)=>{
                    e.stopPropagation();
                    setOpen((o)=>!o);
                },
                "data-testid": Testids.cmColumnMenu.trigger,
                "data-column-id": columnId,
                children: [
                    label,
                    sort ? sort === 'asc' ? /*#__PURE__*/ _jsx(ArrowUp, {
                        "aria-label": "Sorted ascending",
                        className: "size-3.5"
                    }) : /*#__PURE__*/ _jsx(ArrowDown, {
                        "aria-label": "Sorted descending",
                        className: "size-3.5"
                    }) : null,
                    filtered ? /*#__PURE__*/ _jsx(Filter, {
                        "aria-label": "Filtered",
                        className: "size-3.5"
                    }) : null,
                    !sort && !filtered ? /*#__PURE__*/ _jsx(ChevronsUpDown, {
                        "aria-hidden": true,
                        className: "size-3.5 text-muted-foreground opacity-40 group-hover:opacity-100 group-focus-visible:opacity-100"
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraPopover, {
                open: open,
                onOpenChange: setOpen,
                anchor: anchor,
                "aria-label": `${label} menu`,
                className: "w-64",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    onClick: (e)=>e.stopPropagation(),
                    children: [
                        onSortChange ? /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                sortButton('asc', Testids.cmColumnMenu.sortAsc),
                                sortButton('desc', Testids.cmColumnMenu.sortDesc)
                            ]
                        }) : null,
                        filterable ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: cn('max-h-60 space-y-1 overflow-y-auto pt-2', onSortChange && 'border-t'),
                                    children: [
                                        /*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center gap-2 text-sm font-medium",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                                    checked: options.length > 0 && selected.size === options.length,
                                                    onCheckedChange: (on)=>onSelectedChange(new Set(on === true ? options : [])),
                                                    "data-testid": Testids.cmColumnMenu.selectAll
                                                }),
                                                "Select all"
                                            ]
                                        }),
                                        options.map((v)=>// specra-allow raw-element: a label wrapping the checkbox so the whole row toggles it
                                            /*#__PURE__*/ _jsxs("label", {
                                                className: "flex items-center gap-2 text-sm",
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                                        checked: selected.has(v),
                                                        onCheckedChange: (on)=>toggle(v, on === true),
                                                        "data-testid": Testids.cmColumnMenu.option,
                                                        "data-value": v
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "min-w-0 break-words",
                                                        children: v || '(Blank)'
                                                    })
                                                ]
                                            }, v))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "w-full",
                                    disabled: !filtered,
                                    onClick: ()=>onSelectedChange(new Set()),
                                    "data-testid": Testids.cmColumnMenu.clear,
                                    children: "Clear filter"
                                })
                            ]
                        }) : null
                    ]
                })
            })
        ]
    });
}
/** Sort + filter state for a table's columns: `header(id)` is the column's `header`, `apply` the rows to show. */ export function useColumnView(specs, rows) {
    const [sort, setSort] = useState(null);
    const [filters, setFilters] = useState({});
    const filtered = Object.values(filters).some((s)=>s.size > 0);
    const header = (id)=>{
        const spec = specs.find((s)=>s.id === id);
        if (!spec) return id;
        return /*#__PURE__*/ _jsx(ColumnMenu, {
            columnId: id,
            label: spec.label,
            kind: spec.kind,
            options: distinctValues(rows, spec),
            selected: filters[id] ?? EMPTY,
            onSelectedChange: (next)=>setFilters((f)=>({
                        ...f,
                        [id]: next
                    })),
            sort: sort?.id === id ? sort.dir : null,
            onSortChange: spec.filterOnly ? undefined : (dir)=>setSort(dir ? {
                    id,
                    dir
                } : null),
            filterable: !spec.sortOnly
        });
    };
    const apply = (input)=>applyColumnView(input, specs, filters, sort);
    return {
        header,
        apply,
        filtered,
        sorted: sort !== null,
        clearAll: ()=>setFilters({}),
        reset: ()=>{
            setFilters({});
            setSort(null);
        }
    };
}
const EMPTY = new Set();
/** The empty state's way out when column filters hid every row (the headers are gone with the rows). */ export function clearAllAction(view) {
    return view.filtered ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        variant: "ghost",
        onClick: view.clearAll,
        "data-testid": Testids.cmColumnMenu.clearAll,
        children: "Clear column filters"
    }) : undefined;
}
